import {z} from "zod";
import {
  FEATURE_FLAG_PARAMETER,
  FRACTION_PARAMETERS,
  MULTI_VALUE_OPERATORS,
  NO_VALUE_OPERATORS,
  SEGMENT_TYPES,
} from "./constants";
import {splitFlagValue} from "./helpers";

export const conditionSchema = z
  .object({
    parameter: z.string().min(1, "Select a parameter for each condition"),
    operator: z.string().min(1, "Select an operator for each condition"),
    value: z.unknown().nullable(),
  })
  .check((ctx) => {
    const {parameter, operator, value} = ctx.value;
    if (NO_VALUE_OPERATORS.has(operator)) return;
    const addValueError = (message: string) =>
      ctx.issues.push({code: "custom", path: ["value"], message, input: value});

    if (MULTI_VALUE_OPERATORS.has(operator)) {
      if (!Array.isArray(value) || value.length === 0) addValueError("Select at least one value");
      return;
    }
    if (parameter === FEATURE_FLAG_PARAMETER) {
      if (typeof value !== "string" || value.length === 0) {
        addValueError("Required");
        return;
      }
      const {name, variant} = splitFlagValue(value);
      if (!name || !variant) addValueError("Pick a flag and a variant");
      return;
    }
    if (value === null || value === undefined || value === "") {
      addValueError("Required");
      return;
    }
    if (typeof value === "number") {
      if (!Number.isFinite(value)) {
        addValueError("Invalid number");
      } else if (value < 0) {
        addValueError("Must be 0 or greater");
      } else if (FRACTION_PARAMETERS.has(parameter) && value > 1) {
        addValueError("Must be between 0 and 1");
      }
    }
  });

const conditionGroupSchema = z.object({
  name: z.string(),
  match_mode: z.enum(["all", "any"]),
  conditions: z.array(conditionSchema).min(1, "Add at least one condition per group"),
});

export const formSchema = z.object({
  name: z.string().min(1, "Enter a segment name"),
  description: z.string(),
  type: z.enum(SEGMENT_TYPES),
  group_match_mode: z.enum(["all", "any"]),
  groups: z.array(conditionGroupSchema).min(1, "Add at least one group"),
});

export type FormValues = z.infer<typeof formSchema>;
export type ConditionsFormValues = Pick<FormValues, "groups" | "group_match_mode">;
